import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiShoppingCart, FiArrowLeft, FiMinus, FiPlus } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import useGetProduct from '../../../hooks/products/useGetProduct';
import useAddToCart from '../../../hooks/cart/useAddToCart';
import { PRODUCTS } from '../../../lib/routes';
import SuggestedProducts from './SuggestedProducts/SuggestedProducts';

const ProductInfo = () => {
  const { id } = useParams();
  const { product, loading, error } = useGetProduct(id);
  const { cart, addToCart } = useAddToCart();

  const [added, setAdded] = useState(false);
  const [quantityToAdd, setQuantityToAdd] = useState(1);

  useEffect(() => {
    if (added) {
      const timeout = setTimeout(() => setAdded(false), 1200);
      return () => clearTimeout(timeout);
    }
  }, [added]);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen bg-white text-gray-400 animate-pulse">
        Loading product...
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center min-h-screen bg-white text-red-500 font-semibold">
        {error.message}
      </div>
    );

  if (!product) return null;

  const { name, description, price, imageUrl, featured, sku, quantity } = product;

  const cartItem = cart.find((item) => item.sku === (sku || id));
  const quantityInCart = cartItem ? cartItem.quantity : 0;

  const handleQuantityChange = (type) => {
    setQuantityToAdd((prev) => {
      if (type === 'increment' && prev < quantity) return prev + 1;
      if (type === 'decrement' && prev > 1) return prev - 1;
      return prev;
    });
  };

  const handleAddToCart = () => {
    const currentQty = cartItem?.quantity || 0;
    const totalAfterAdd = currentQty + quantityToAdd;
    if (totalAfterAdd > quantity) return;

    addToCart(
      {
        id,
        sku: sku || id,
        name,
        price,
        description,
        imageUrl,
      },
      quantityToAdd
    );
    setAdded(true);
  };

  return (
    <div className="bg-white text-gray-900 min-h-screen py-16 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-start">
        {/* Product Image */}
        <div className="w-full overflow-hidden rounded-3xl shadow-lg border border-gray-200">
          <img
            src={imageUrl || 'https://via.placeholder.com/600x400?text=No+Image'}
            alt={name}
            className="w-full h-full object-cover transition duration-300 ease-in-out hover:scale-105"
            loading="lazy"
          />
        </div>

        {/* Product Info */}
        <div className="flex flex-col space-y-6 relative">
          <Link
            to={PRODUCTS}
            className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-500 transition mb-2"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>

          {featured && (
            <span className="inline-block bg-purple-600 text-xs uppercase font-semibold text-white px-3 py-1 rounded-full shadow w-fit">
              Featured
            </span>
          )}

          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{name}</h1>

          <p className="text-2xl text-purple-600 font-semibold">${price.toFixed(2)}</p>

          {quantity > 0 && quantity <= 3 && (
            <p className="text-sm text-red-500 font-medium">Only {quantity} left — order soon!</p>
          )}
          {quantity === 0 && (
            <p className="text-sm text-red-600 font-semibold uppercase">Out of Stock</p>
          )}

          <p className="text-gray-700 text-lg leading-relaxed">{description}</p>

          {/* Quantity Selector + Add to Cart */}
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
              <button
                type="button"
                onClick={() => handleQuantityChange('decrement')}
                disabled={quantityToAdd <= 1}
                className="p-3 text-xl text-gray-700 hover:text-gray-900 hover:bg-gray-200 transition"
              >
                <FiMinus />
              </button>
              <span className="px-6 text-lg font-semibold select-none">{quantityToAdd}</span>
              <button
                type="button"
                onClick={() => handleQuantityChange('increment')}
                disabled={quantityToAdd >= quantity}
                className="p-3 text-xl text-gray-700 hover:text-gray-900 hover:bg-gray-200 transition"
              >
                <FiPlus />
              </button>
            </div>

            <button
              type="button"
              onClick={handleAddToCart}
              disabled={quantity === 0}
              className={`relative inline-flex items-center justify-center gap-3 px-10 py-4 rounded-2xl text-lg font-bold transition-all shadow-md
                ${
                  quantity === 0
                    ? 'bg-gray-300 cursor-not-allowed text-gray-600'
                    : 'bg-purple-600 hover:bg-purple-700 text-white active:scale-95'
                }`}
            >
              <FiShoppingCart className="w-6 h-6" />
              {quantityInCart > 0
                ? `In Cart: ${quantityInCart}`
                : `Add ${quantityToAdd} to Cart`}

              <AnimatePresence>
                {added && (
                  <motion.span
                    key="added"
                    initial={{ scale: 0, opacity: 0, y: 10 }}
                    animate={{ scale: 1.5, opacity: 1, y: -10 }}
                    exit={{ scale: 0, opacity: 0, y: -60 }}
                    transition={{ duration: 0.6, type: 'spring', bounce: 0.6 }}
                    className="absolute right-4 top-0 text-3xl select-none pointer-events-none"
                  >
                    💖
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* Suggested Products Section */}
      <SuggestedProducts productId={id} />
    </div>
  );
};

export default ProductInfo;
